import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Archive, ArrowLeft, RotateCcw, Trash2 } from 'lucide-react';
import {
  useArchivedOrganizations,
  useHardDeleteOrganization,
  useRestoreOrganization,
} from '@repo/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { BusinessAvatar, brandingColor } from '@/features/avatar';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function BusinessesArchivedScreen() {
  const { canArchiveOrg } = useActiveRole();
  const { organizations, loading } = useArchivedOrganizations();
  const restore = useRestoreOrganization();
  const hardDelete = useHardDeleteOrganization();
  const { toast } = useToast();

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');

  if (!canArchiveOrg) {
    return <Navigate to="/settings/businesses" replace />;
  }

  const target = organizations?.find((o) => o.id === deletingId) ?? null;

  return (
    <Page>
      <Button variant="link" size="sm" asChild className="-ml-2 mb-4">
        <Link to="/settings/businesses">
          <ArrowLeft className="size-4" /> Back to businesses
        </Link>
      </Button>
      <PageHeader
        title="Archived businesses"
        description="Restore within 30 days, or permanently delete to free your billing seat."
      />

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, idx) => (
            <Skeleton key={idx} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : !organizations || organizations.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Archive className="size-7" />}
              title="No archived businesses"
              description="Anything you archive will appear here for 30 days."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((org) => (
            <Card key={org.id}>
              <CardContent className="flex flex-col gap-3 p-5">
                <div className="flex items-center gap-3">
                  <BusinessAvatar
                    id={org.id}
                    name={org.name}
                    initials={org.nickname ? org.nickname.slice(0, 3).toUpperCase() : undefined}
                    color={brandingColor({ id: org.id, branding: org.branding })}
                    logoUrl={org.logoUrl ?? null}
                    size={40}
                  />
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold text-foreground">
                      {org.nickname || org.name}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {org.deleteAt
                        ? `Deletes ${new Date(org.deleteAt).toLocaleDateString()}`
                        : 'Archived'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={async () => {
                      try {
                        await restore.restoreOrganization(org.id);
                        toast({ title: 'Business restored', variant: 'success' });
                      } catch (err) {
                        const desc = describeAppError(err);
                        toast({
                          title: desc.title,
                          description: desc.message,
                          variant: 'danger',
                        });
                      }
                    }}
                  >
                    <RotateCcw className="size-4" /> Restore
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => {
                      setDeletingId(org.id);
                      setConfirmText('');
                    }}
                  >
                    <Trash2 className="size-4" /> Delete forever
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={!!deletingId}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingId(null);
            setConfirmText('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently delete {target?.nickname || target?.name}?</DialogTitle>
            <DialogDescription>
              This cannot be undone. Every client, invoice, payment, and team member
              will be removed.
            </DialogDescription>
          </DialogHeader>
          <Field
            label={`Type "${target?.name ?? ''}" to confirm`}
            htmlFor="confirm"
          >
            <Input
              id="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeletingId(null);
                setConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={hardDelete.loading}
              disabled={!target || confirmText !== target.name}
              onClick={async () => {
                if (!target) return;
                try {
                  await hardDelete.hardDeleteOrganization(target.id);
                  toast({ title: 'Business deleted', variant: 'success' });
                  setDeletingId(null);
                  setConfirmText('');
                } catch (err) {
                  const desc = describeAppError(err);
                  toast({
                    title: desc.title,
                    description: desc.message,
                    variant: 'danger',
                  });
                }
              }}
            >
              Delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
